import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  loading: false,
  validating: false,
  data: [],
  isDragging: false,
  selectedCell: null,
  detailPanelExpandedRowIds: [],
  editingCell: null,
  isSelectOpen: false,
  customState: {}, // Generic state that can be extended by different import types
  deleteModal: { open: false, itemId: null },
  importModal: { open: false, count: 0 }
};

export const useImportStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // Reset store to initial state (useful when switching between import types)
      reset: () => set(initialState, false, 'reset'),

      // Loading states
      setLoading: (loading) => set({ loading }, false, 'setLoading'),
      setValidating: (validating) => set({ validating }, false, 'setValidating'),

      // Data management
      setData: (data) => set({ data }, false, 'setData'),
      updateItem: (id, updates) => {
        set(
          (state) => ({
            data: state.data.map(item =>
              item.id === id ? { ...item, ...updates } : item
            )
          }),
          false,
          'updateItem'
        );
      },
      deleteItem: (id) => set(
        (state) => ({
          data: state.data.filter(item => item.id !== id)
        }),
        false,
        'deleteItem'
      ),

      // UI states
      setDragging: (isDragging) => set({ isDragging }, false, 'setDragging'),
      setSelectedCell: (selectedCell) => set({ selectedCell }, false, 'setSelectedCell'),
      setEditingCell: (editingCell) => set({ editingCell }, false, 'setEditingCell'),
      setSelectOpen: (isSelectOpen) => set({ isSelectOpen }, false, 'setSelectOpen'),

      // Custom state management
      setCustomState: (updates) => set(
        (state) => ({
          customState: typeof updates === 'function' 
            ? updates(state.customState)
            : { ...state.customState, ...updates }
        }),
        false,
        'setCustomState'
      ),

      // Detail panel management
      toggleDetailPanel: (id) => set(
        (state) => ({
          detailPanelExpandedRowIds: state.detailPanelExpandedRowIds.includes(id)
            ? state.detailPanelExpandedRowIds.filter(rowId => rowId !== id)
            : [...state.detailPanelExpandedRowIds, id]
        }),
        false,
        'toggleDetailPanel'
      ),

      // Modal states
      setDeleteModal: (deleteModal) => set({ deleteModal }, false, 'setDeleteModal'),
      setImportModal: (importModal) => set({ importModal }, false, 'setImportModal'),

      // Helper methods
      getItem: (id) => {
        const state = get();
        return state.data.find(item => item.id === id);
      },

      getItemsToImport: (config) => {
        const state = get();
        return config.helpers?.getImportableItems?.(state.data) || 
               state.data.filter(item => item.validationStatus === 'valid');
      },

      // Batch operations
      updateMultipleItems: (updates) => set(
        (state) => ({
          data: state.data.map(item => {
            const update = updates.find(u => u.id === item.id);
            return update ? { ...item, ...update.data } : item;
          })
        }),
        false,
        'updateMultipleItems'
      ),

      // Clear specific data (useful for cleanup)
      clearData: () => set({ data: [] }, false, 'clearData'),
      clearModals: () => set({ 
        deleteModal: { open: false, itemId: null },
        importModal: { open: false, count: 0 }
      }, false, 'clearModals')
    }),
    {
      name: 'import-store', // Store name for devtools
    }
  )
);

// Selector hooks for common patterns
export const useImportData = () => useImportStore((state) => state.data);
export const useImportLoading = () => useImportStore((state) => ({ 
  loading: state.loading, 
  validating: state.validating 
}));
export const useImportUI = () => useImportStore((state) => ({
  editingCell: state.editingCell,
  isSelectOpen: state.isSelectOpen,
  selectedCell: state.selectedCell,
  isDragging: state.isDragging
}));
export const useImportModals = () => useImportStore((state) => ({
  deleteModal: state.deleteModal,
  importModal: state.importModal
}));
export const useImportCustomState = () => useImportStore((state) => state.customState); 